/**
 * MODULE: domain/sales-intelligence/frontend/components/TypingIndicator.tsx
 * PURPOSE: Loading indicator component showing research progress with animated typing dots
 * 
 * DEPENDENCIES:
 *   - react: React library
 * 
 * PUBLIC API:
 *   - TypingIndicator: Loading state component
 * 
 * SIDE EFFECTS:
 *   - None (presentational only)
 * 
 * AGENT SAFETY: SAFE
 * FILE SIZE: 66 / 200 lines
 * 
 * See: /README.md#documentation | /ARCHITECTURE.md | /domain/sales-intelligence/README.md
 */

import React from 'react';

interface TypingIndicatorProps {
  progress: number;
  currentStep: string;
  output: string;
}

export function TypingIndicator({ progress, currentStep, output }: TypingIndicatorProps) {
  return (
    <div className="flex justify-start">
      <div className="max-w-[70%] px-4 py-3 rounded-lg bg-white border border-gray-200">
        <div className="flex items-center gap-2 mb-2">
          <div className="flex gap-1">
            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
            <div 
              className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" 
              style={{ animationDelay: '0.2s' }}
            ></div>
            <div 
              className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" 
              style={{ animationDelay: '0.4s' }}
            ></div>
          </div>
          <span className="text-sm text-gray-600">Researching...</span>
        </div>
        
        {/* Progress bar */}
        <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
          <div
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
        
        <p className="text-xs text-gray-500">{currentStep}</p>
        
        {output && (
          <p className="text-sm text-gray-700 mt-2 line-clamp-2">
            {output.slice(0, 150)}
            {output.length > 150 ? '...' : ''}
          </p>
        )}
      </div>
    </div>
  );
}
